import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  useMediaQuery, 
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Button,
  Box,
  Typography,
  Stack
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { format, isToday, getDay, addDays, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { esFeriadoPeruano, obtenerNombreFeriado } from '../../utils/feriadosPeruanos';
import citaService from '../../services/citaService';
import { isMobileDevice } from '../../utils/isAndroidWebView';

// Componente para celda de hora en vista semanal
const HourCell = React.memo(({ 
  hour, 
  day, 
  events, 
  isToday, 
  onDateClick, 
  onEventClick, 
  colorConfig,
  selectedEspecialista,
  selectedEspecialidad,
  isHoraBloqueada,
  onEliminarCita,
  onEliminarCitaYPosteriores,
  canCreate,
  showNotification,
  getUserRole
}) => {
  const navigate = useNavigate();
  const isLowPerf = isMobileDevice();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCita, setSelectedCita] = useState(null);

  const dateStr = format(day, 'yyyy-MM-dd');
  const hourNum = typeof hour === 'string' ? parseInt(hour.split(':')[0]) : hour;
  const hourStr = typeof hour === 'string' ? hour : `${hour.toString().padStart(2, '0')}:00`;

  const hourEvents = events.filter(event => {
    const eventFecha = typeof event.fecha === 'string' 
      ? event.fecha 
      : (event.fecha instanceof Date ? format(event.fecha, 'yyyy-MM-dd') : event.fecha);
    
    const sameDay = eventFecha === dateStr;
    const eventHour = event.horaInicioNum;
    const eventTime = event.horaInicio || '00:00';
    return sameDay && eventHour === hourNum && eventTime.startsWith(hourStr.substring(0, 5));
  });

  const esFeriado = esFeriadoPeruano(dateStr);
  const nombreFeriado = esFeriado ? obtenerNombreFeriado(dateStr) : null;

  const horarioInfo = selectedEspecialista ? 
    isHoraBloqueada(dateStr, hourStr, selectedEspecialista) : 
    { bloqueada: false, laborable: true, esAdministrativa: false, motivo: '' };

  const handleCitaRightClick = (event, cita) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCita(cita);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCita(null);
  };

  const handleEmptyClick = () => {
    if (horarioInfo.laborable && !esFeriado) {
      const clickDate = new Date(day);
      const [h, m] = hourStr.split(':').map(Number);
      clickDate.setHours(h, m || 0, 0, 0);
      onDateClick(clickDate, hourNum);
    }
  };

  // Estilos simplificados para mejor rendimiento
  let backgroundColor = '#fff';
  let borderColor = '#e0e0e0';

  if (esFeriado) {
    backgroundColor = '#ffebee';
    borderColor = '#ef5350';
  } else if (selectedEspecialista) {
    if (horarioInfo.esAdministrativa) {
      backgroundColor = '#7B1FA2';
    } else if (horarioInfo.laborable) {
      backgroundColor = '#fffcd4';
    } else {
      backgroundColor = '#e0e0e0';
    }
    
    if (isToday) {
      borderColor = '#2196f3';
    }
  } else if (isToday) {
    backgroundColor = '#e3f2fd';
    borderColor = '#2196f3';
  }
  
  const cellStyle = {
    width: '100%',
    height: '60px',
    border: `1px solid ${borderColor}`,
    background: backgroundColor,
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    cursor: (horarioInfo.laborable && !esFeriado) ? 'pointer' : 'not-allowed',
    borderRadius: '4px',
    margin: '1px',
    color: horarioInfo.esAdministrativa ? '#fff' : 'inherit'
  };
  
  return (
    <>
      <div 
        style={cellStyle}
        onClick={hourEvents.length === 0 ? handleEmptyClick : undefined}
        title={esFeriado ? `Feriado: ${nombreFeriado}` : 
               !horarioInfo.laborable ? `No laborable: ${horarioInfo.motivo}` : 
               hourEvents.length === 0 ? 'Click para crear cita' : 'Citas programadas'}
      >
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          padding: '4px',
          overflow: 'hidden'
        }}>
          {/* Indicador de feriado */}
          {esFeriado && hourEvents.length === 0 && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '10px',
              fontWeight: '700',
              color: '#d32f2f',
              textAlign: 'center'
            }}>
              🎊 FERIADO
            </div>
          )}
          
          {/* Botón para crear cita */}
          {hourEvents.length === 0 && horarioInfo.laborable && !esFeriado && canCreate() && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: 0.5
            }}>
              <AddIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            </div>
          )}
          
          {/* Renderizado de citas */}
          {selectedEspecialista && hourEvents.length > 0 ? (
            <div 
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                background: hourEvents[0].color,
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                padding: '4px',
                fontSize: '10px',
                fontWeight: '600',
                cursor: 'pointer',
                borderRadius: '4px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onEventClick(hourEvents[0]);
              }}
              onContextMenu={(e) => handleCitaRightClick(e, hourEvents[0])}
            >
              <div style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap', 
                width: '100%'
              }}>
                {hourEvents[0].title}
              </div>
              {hourEvents[0].etapaNombre && (
                <div style={{ fontSize: '8px', opacity: 0.9, marginTop: '2px' }}>
                  {hourEvents[0].etapaNombre}
                </div>
              )}
              {hourEvents.length > 1 && (
                <div style={{ fontSize: '8px', opacity: 0.8 }}>
                  +{hourEvents.length - 1} más
                </div>
              )}
              
              {/* Botón de menú contextual */}
              <div 
                style={{ 
                  position: 'absolute', 
                  top: '2px', 
                  right: '2px',
                  opacity: 0.7
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCitaRightClick(e, hourEvents[0]);
                }}
              >
                <MoreVertIcon sx={{ fontSize: 10 }} />
              </div>
            </div>
          ) : (
            <>
              {/* Indicadores para diferentes tipos de horas */}
              {selectedEspecialista && hourEvents.length === 0 && !esFeriado && (
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  fontSize: '10px'
                }}>
                  {horarioInfo.esAdministrativa ? '📋' : !horarioInfo.laborable ? '🚫' : null}
                </div>
              )}
              
              {/* Eventos como bloques pequeños */}
              {hourEvents.map((event) => (
                <div 
                  key={event.id}
                  style={{
                    background: event.color,
                    color: 'white',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    marginBottom: '2px',
                    fontSize: '10px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                  }}
                  onContextMenu={(e) => handleCitaRightClick(e, event)}
                >
                  {event.title}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Menú contextual */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          sx: { borderRadius: '8px', minWidth: '160px' }
        }}
      >
        <MenuItem onClick={() => navigate(`/citas/${selectedCita?.id}`)}>
          <ListItemIcon><VisibilityIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Ver Detalle" />
        </MenuItem>
        
        <MenuItem onClick={() => navigate(`/citas/editar/${selectedCita?.id}`)}>
          <ListItemIcon><EditIcon fontSize="small" color="secondary" /></ListItemIcon>
          <ListItemText primary="Editar" />
        </MenuItem>
        
        {canCreate() && selectedCita && (
          <>
            <MenuItem 
              onClick={() => {
                if (window.confirm('¿Está seguro de que desea eliminar permanentemente esta cita?')) {
                  onEliminarCita(selectedCita.id);
                }
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary="Eliminar" />
            </MenuItem>
            
            <MenuItem 
              onClick={() => onEliminarCitaYPosteriores(selectedCita.id)}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><DeleteSweepIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary="Eliminar Esta y Posteriores" />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
});

const WeekView = ({
  currentDate,
  events,
  onDateClick,
  onEventClick,
  colorConfig,
  selectedEspecialista,
  selectedEspecialidad,
  isHoraBloqueada,
  horariosNoLaborables,
  showNotification,
  refreshEvents,
  getUserRole
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLowPerf = isMobileDevice();

  const [eliminarPosterioresModalOpen, setEliminarPosterioresModalOpen] = useState(false);
  const [citaParaEliminarPosteriores, setCitaParaEliminarPosteriores] = useState(null);
  const [loadingEliminarPosteriores, setLoadingEliminarPosteriores] = useState(false);
  const [citasPosterioresInfo, setCitasPosterioresInfo] = useState(null);

  const canCreate = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      let dateToFormat = dateString;
      if (dateString.includes('T')) {
        dateToFormat = dateString.split('T')[0];
      }
      const [year, month, day] = dateToFormat.split('-');
      const date = new Date(year, month - 1, day, 12, 0, 0);
      return format(date, 'dd/MM/yyyy', { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  const handleEliminarCita = async (citaId) => {
    try {
      await citaService.deleteCita(citaId);
      showNotification('Cita eliminada permanentemente', 'success');
      refreshEvents();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      showNotification('Error al eliminar la cita', 'error');
    }
  };

  const handleEliminarCitaYPosteriores = async (citaId) => {
    try {
      const conteoInfo = await citaService.contarCitasPosteriores(citaId);
      
      if (conteoInfo.total === 0) {
        showNotification('No se encontraron citas posteriores para eliminar', 'warning');
        return;
      }
      
      const citaEncontrada = events.find(event => event.id === citaId);
      
      setCitaParaEliminarPosteriores(citaEncontrada || { id: citaId });
      setCitasPosterioresInfo({
        totalCitas: conteoInfo.total,
        paciente: conteoInfo.paciente,
        fechaDesde: conteoInfo.fecha_desde,
        ejemplos: conteoInfo.ejemplos || [],
        esInformacionCompleta: true
      });
      
      setEliminarPosterioresModalOpen(true);
    } catch (error) {
      console.error('Error al consultar citas posteriores:', error);
      showNotification('Error al consultar citas posteriores', 'error');
    }
  };

  const handleConfirmarEliminacionPosteriores = async () => {
    if (!citaParaEliminarPosteriores) return;
    
    setLoadingEliminarPosteriores(true);
    try {
      const result = await citaService.eliminarCitaYPosteriores(citaParaEliminarPosteriores.id);
      
      showNotification(
        `${result.citas_eliminadas || 0} citas eliminadas exitosamente`, 
        'success'
      );
      
      setEliminarPosterioresModalOpen(false);
      setCitaParaEliminarPosteriores(null);
      setCitasPosterioresInfo(null);
      refreshEvents();
    } catch (error) {
      console.error('Error eliminando citas posteriores:', error);
      showNotification('Error al eliminar las citas posteriores', 'error');
    } finally {
      setLoadingEliminarPosteriores(false);
    }
  };

  // Función para obtener las horas según el especialista
  const getHorasDisponibles = () => {
    if (!selectedEspecialista || !horariosNoLaborables) {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    let horarios = horariosNoLaborables.get(parseInt(selectedEspecialista));
    
    if (typeof horarios === 'string') {
      try {
        horarios = JSON.parse(horarios);
      } catch (error) {
        return Array.from({ length: 12 }, (_, i) => i + 8);
      }
    }
    
    if (!horarios) {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    const horariosGenerales = horarios.horarios_generales || horarios;
    
    if (!horariosGenerales || typeof horariosGenerales !== 'object') {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    const diasSemana = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    const todasLasHoras = new Set();
    
    diasSemana.forEach(dia => {
      const horariosDia = horariosGenerales[dia];
      if (horariosDia && typeof horariosDia === 'object') {
        Object.keys(horariosDia).forEach(hora => {
          todasLasHoras.add(hora);
        });
      }
    });
    
    if (todasLasHoras.size === 0) {
      return Array.from({ length: 12 }, (_, i) => i + 8);
    }
    
    const horasArray = Array.from(todasLasHoras);
    
    return horasArray.sort((a, b) => {
      const [aHour, aMin] = a.split(':').map(Number);
      const [bHour, bMin] = b.split(':').map(Number);
      return (aHour * 60 + aMin) - (bHour * 60 + bMin);
    });
  };

  const hours = getHorasDisponibles();
  
  // Calcular semana SIN domingo
  const day = getDay(currentDate);
  const diff = day === 0 ? 6 : day - 1;
  let weekStart = subDays(currentDate, diff);
  
  if (getDay(weekStart) === 0) {
    weekStart = addDays(weekStart, 1);
  }
  
  const weekDays = [];
  for (let i = 0; i < 6; i++) {
    const dayToAdd = addDays(weekStart, i);
    if (getDay(dayToAdd) !== 0) {
      weekDays.push(dayToAdd);
    }
  }
  
  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      {/* Encabezado */}
      <div style={{ 
        display: 'flex',
        width: '100%',
        borderRadius: '8px 8px 0 0',
        overflow: 'hidden',
        marginBottom: '4px'
      }}>
        <div style={{ 
          width: '50px',
          background: colorConfig.shouldUseSpecialtyColors ? colorConfig.headerColor : '#f8f9fa',
          color: colorConfig.shouldUseSpecialtyColors ? colorConfig.headerTextColor : '#1a202c',
          border: '1px solid #e0e0e0',
          borderBottom: 'none',
          borderRight: 'none'
        }}></div>
        
        {weekDays.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const esFeriado = esFeriadoPeruano(dateStr);
          const nombreFeriado = esFeriado ? obtenerNombreFeriado(dateStr) : null;
          
          return (
            <div key={day.toISOString()} style={{
              flex: '1 1 0',
              padding: isMobile ? '6px 2px' : '10px 6px',
              textAlign: 'center',
              background: esFeriado ? '#ffebee' :
                colorConfig.shouldUseSpecialtyColors ? colorConfig.headerColor : 
                (isToday(day) ? '#e3f2fd' : '#f8f9fa'),
              color: esFeriado ? '#d32f2f' : 
                     colorConfig.shouldUseSpecialtyColors ? colorConfig.headerTextColor : '#1a202c',
              border: esFeriado ? '2px solid #ef5350' : '1px solid #e0e0e0',
              borderBottom: 'none',
              borderLeft: 'none',
              fontSize: isMobile ? '0.65rem' : '0.8rem',
              fontWeight: '600'
            }}>
              <div style={{ fontWeight: isToday(day) ? '700' : '600' }}>
                {format(day, 'EEE', { locale: es })}
                {esFeriado && ' 🎊'}
              </div>
              <div style={{ fontSize: isMobile ? '0.6rem' : '0.75rem', opacity: 0.9 }}>
                {format(day, 'd MMM', { locale: es })}
              </div>
              {esFeriado && (
                <div style={{ fontSize: '7px', fontStyle: 'italic', marginTop: '2px', color: '#b71c1c' }}>
                  {nombreFeriado}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Filas de horas */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {hours.map(hour => {
          const hourStr = typeof hour === 'string' ? hour : `${hour.toString().padStart(2, '0')}:00`;
          const [h, m] = hourStr.split(':').map(Number);
          const period = h >= 12 ? 'PM' : 'AM';
          const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
          const displayHour = m > 0 ? `${hour12}:${m.toString().padStart(2, '0')}` : `${hour12}`;
          
          return (
            <div key={hourStr} style={{ 
              display: 'flex',
              width: '100%',
              height: '60px',
              gap: '1px'
            }}>
              <div style={{ 
                width: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 4px',
                background: '#f5f5f5',
                border: '1px solid #e0e0e0',
                fontSize: '10px',
                fontWeight: '600',
                color: '#666',
                borderRadius: '4px'
              }}>
                {displayHour} {period}
              </div>
              
              {weekDays.map((day) => (
                <div key={`${day.toISOString()}-${hourStr}`} style={{ flex: '1 1 0' }}>
                  <HourCell 
                    hour={hour}
                    day={day}
                    events={events}
                    isToday={isToday(day)}
                    onDateClick={onDateClick}
                    onEventClick={onEventClick}
                    colorConfig={colorConfig}
                    selectedEspecialista={selectedEspecialista}
                    selectedEspecialidad={selectedEspecialidad}
                    isHoraBloqueada={isHoraBloqueada}
                    onEliminarCita={handleEliminarCita}
                    onEliminarCitaYPosteriores={handleEliminarCitaYPosteriores}
                    canCreate={canCreate}
                    showNotification={showNotification}
                    getUserRole={getUserRole}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    
      {/* Leyenda - solo en desktop */}
      {selectedEspecialista && !isLowPerf && (
        <div style={{ 
          marginTop: '16px', 
          padding: '16px', 
          background: '#f5f5f5', 
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#333' }}>
            Leyenda:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {[
              { color: '#FF9800', label: 'Consulta' },
              { color: '#AD1457', label: 'Evaluación' },
              { color: '#4CAF50', label: 'Informe' },
              { color: '#00695C', label: 'Terapia' },
              { color: '#FFC107', label: 'Grupales/Talleres' },
              { color: '#1976D2', label: 'Otros' },
              { color: '#FFFCD4', border: '1px solid #ddd', label: 'Disponible' },
              { color: '#ffebee', border: '2px dashed #f44336', label: '🎊 Feriado' }
            ].map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ 
                  width: '14px', 
                  height: '14px', 
                  background: item.color,
                  border: item.border || 'none',
                  borderRadius: '3px'
                }} />
                <span style={{ fontSize: '11px', color: '#666' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de eliminación */}
      <Dialog
        open={eliminarPosterioresModalOpen}
        onClose={() => {
          setEliminarPosterioresModalOpen(false);
          setCitaParaEliminarPosteriores(null);
          setCitasPosterioresInfo(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ background: '#ffebee', color: '#d32f2f', fontWeight: '700' }}>
          Eliminar Cita y Todas las Posteriores
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>¡Atención!</strong> Esta acción eliminará permanentemente la cita seleccionada 
            y todas las citas posteriores del mismo paciente con el mismo especialista.
          </Alert>
          
          {citaParaEliminarPosteriores && citasPosterioresInfo && (
            <Box sx={{ background: '#f5f5f5', borderRadius: '8px', padding: '16px' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: '600' }}>
                Detalles de la eliminación:
              </Typography>
              
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Paciente:</strong> {citasPosterioresInfo.paciente || 
                    (citaParaEliminarPosteriores.pacienteNombre || 'Sin información')}
                </Typography>
                
                <Typography variant="body2">
                  <strong>Desde la fecha:</strong> {formatDate(
                    citasPosterioresInfo.fechaDesde || citaParaEliminarPosteriores.fecha
                  )}
                </Typography>
                
                <Typography variant="body2" color="error" sx={{ fontSize: '1.1rem', fontWeight: '700' }}>
                  <strong>Total de citas a eliminar: {citasPosterioresInfo.totalCitas}</strong>
                </Typography>
                
                {citasPosterioresInfo.ejemplos && citasPosterioresInfo.ejemplos.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2" gutterBottom sx={{ fontWeight: '600' }}>
                      <strong>Ejemplos:</strong>
                    </Typography>
                    {citasPosterioresInfo.ejemplos.map((cita, index) => (
                      <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                        • {formatDate(cita.fecha)} - {cita.hora} ({cita.estado})
                      </Typography>
                    ))}
                    {citasPosterioresInfo.totalCitas > citasPosterioresInfo.ejemplos.length && (
                      <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic', color: 'primary.main' }}>
                        ... y {citasPosterioresInfo.totalCitas - citasPosterioresInfo.ejemplos.length} citas más
                      </Typography>
                    )}
                  </Alert>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setEliminarPosterioresModalOpen(false);
              setCitaParaEliminarPosteriores(null);
              setCitasPosterioresInfo(null);
            }}
            disabled={loadingEliminarPosteriores}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarEliminacionPosteriores}
            variant="contained"
            color="error"
            disabled={loadingEliminarPosteriores || !citasPosterioresInfo?.totalCitas}
            startIcon={loadingEliminarPosteriores ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
          >
            {loadingEliminarPosteriores ? 'Eliminando...' : `Eliminar ${citasPosterioresInfo?.totalCitas || 0} Citas`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WeekView;
